import React, { useEffect, useRef, useState } from "react";

import { Colors } from "../../../assets/Colors";
import { fetchGet, fetchPost } from "../../../fetchers";
import { useMutation } from "@tanstack/react-query";
import { Card } from "@material-tailwind/react";
import { isRequired, useForm } from "../../../components/isRequired";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import "react-toastify/dist/ReactToastify.css";
import { BiArrowBack, BiCheckCircle, BiXCircle, BiFile, BiImage, BiX, BiLoader } from "react-icons/bi";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const ConfirmModal = ({
  show, onConfirm, onCancel, type, reason, onReasonChange,
  jumlahDibatalkan = 0, jumlahDisetujui = 0,
}) => {
  if (!show) return null;
  const isApprove = type === "approve";
  const reasonEmpty = !isApprove && !(reason || "").trim();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
        <div className={`flex items-center gap-3 mb-4 ${isApprove ? "text-green-600" : "text-red-500"}`}>
          {isApprove ? <BiCheckCircle className="text-3xl" /> : <BiXCircle className="text-3xl" />}
          <h3 className="text-lg font-semibold text-gray-800">
            {isApprove ? "Konfirmasi Approve" : "Konfirmasi Tolak"}
          </h3>
        </div>
        <p className="text-gray-600 mb-4 text-sm">
          {isApprove
            ? "Apakah Anda yakin ingin menyetujui Purchase Requisition ini?"
            : "Apakah Anda yakin ingin menolak Purchase Requisition ini?"}
        </p>
        {isApprove && jumlahDibatalkan > 0 && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <p className="font-semibold">
              {jumlahDisetujui} item disetujui, {jumlahDibatalkan} item akan dibatalkan.
            </p>
            <p className="mt-1">
              Item yang dibatalkan tidak akan diproses pembeliannya dan
              <b> tidak dapat dihidupkan kembali</b> oleh approval berikutnya.
            </p>
          </div>
        )}
        {!isApprove && (
          <div className="mb-6">
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Alasan penolakan <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason || ""}
              onChange={(e) => onReasonChange(e.target.value)}
              rows={3}
              placeholder="Tuliskan alasan penolakan agar pemohon mengetahuinya…"
              className="w-full text-sm border border-gray-300 rounded-lg p-2 outline-none focus:border-red-400 resize-none"
            />
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Batal
          </button>
          <button
            onClick={onConfirm}
            disabled={reasonEmpty}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              isApprove ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"
            } ${reasonEmpty ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {isApprove ? "Ya, Approve" : "Ya, Tolak"}
          </button>
        </div>
      </div>
    </div>
  );
};

const FieldGroup = ({ children, cols = 3 }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${cols} gap-x-4`}>
    {children}
  </div>
);

const SectionTitle = ({ title }) => (
  <div className="flex items-center gap-2 mb-3 mt-5">
    <div className="h-5 w-1 rounded bg-blue-500" />
    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h3>
  </div>
);

const ApprovalPurchaseRequisitionPage = ({}) => {
  let initialState = {
    BisnisUnitID: "",
    DatePR: new Date(),
  };

  let initialStateModal = {
    SDTDate: new Date(),
  };

  const validations = [
    ({ UserPCName }) =>
      isRequired(UserPCName) || { UserPCName: "Nama UserPC wajib diisi" },
    ({ Status }) => isRequired(Status) || { Status: "Status wajib diisi" },
    ({ KelompokID }) =>
      isRequired(KelompokID) || { KelompokID: "Kelompok wajib diisi" },
    ({ DetailAccountID }) =>
      isRequired(DetailAccountID) || {
        DetailAccountID: "Detail Account wajib diisi",
      },
  ];

  const {
    values,
    touched,
    customHandler,
    changeHandler,
    errors,
    blastChange,
  } = useForm(initialState, validations);

  const barang = useMutation((formData) => fetchGet("Master/Barang/min"));
  const [buttonTouched, setButtonTouched] = useState(false);
  const [detail, setDetail] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [modalDetail, setModalDetail] = useState(initialStateModal);
  const [modalID, setModalID] = useState(0);
  const [isJasa, setisJasa] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ show: false, type: null });
  const [rejectReason, setRejectReason] = useState("");

  // PRListID (string) item yang dicoret approver. Berisi juga item yang sudah
  // dibatalkan di level approval sebelumnya — keduanya sama-sama terkunci.
  const [itemBatal, setItemBatal] = useState(() => new Set());

  // Item ber-IsStock TIDAK bisa dibatalkan: stoknya sudah dipotong dan
  // GoodsExpenditure sudah terbit saat MSR DIKIRIM, sebelum layar ini dibuka.
  // Item yang sudah dibatalkan sebelumnya juga terkunci (pembatalan satu arah).
  const terkunci = (item) => Boolean(item.IsStock) || Boolean(item.Dibatalkan);

  const toggleItemBatal = (item) => {
    if (terkunci(item)) return;
    const id = String(item.PRListID);
    // Salin Set-nya; memutasi state langsung tidak memicu render ulang.
    setItemBatal((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Centang semua / hilangkan centang semua — HANYA item yang bisa di-toggle.
  // Item terkunci tidak disentuh: IsStock tetap tercentang, item yang sudah
  // dibatalkan approval sebelumnya tetap tidak tercentang (tak bisa dihidupkan).
  const setAllItemBatal = (batalkan) => {
    setItemBatal((prev) => {
      const next = new Set(prev);
      detail.forEach((item) => {
        if (terkunci(item)) return;
        const id = String(item.PRListID);
        if (batalkan) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  // Stock tersedia = (Saldo Akhir + Qty Transit) - Min Qty
  const calculateAvailableStock = (saldoAkhir, minQty, qtyTransit = 0) => {
    const saldo = parseFloat(saldoAkhir) || 0;
    const min = parseFloat(minQty) || 0;
    const transit = parseFloat(qtyTransit) || 0;
    const available = saldo + transit - min;
    return available > 0 ? available : 0;
  };

  const calculateJumlahPO = (availableStock, qty) => {
    const stockNum = parseFloat(availableStock) || 0;
    const qtyNum = parseFloat(qty) || 0;
    return stockNum >= qtyNum ? 0 : qtyNum;
  };

  const updateAllData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => {
      const availableStock = calculateAvailableStock(
        item.SaldoAkhir,
        item.MinQty,
        item.AturanTransit === false ? 0 : item.QtyTransit
      );
      const jumlahPO = calculateJumlahPO(availableStock, item.Qty);
      // Item yang sudah keluar gudang (IsStock) atau ditahan menunggu barang diterima
      // (MenungguBarang) tidak dibeli: keputusan server menang atas hitungan layar.
      const tidakDibeli = item.IsStock || item.MenungguBarang;
      return {
        ...item,
        Stock: availableStock,
        AvailableStock: availableStock,
        JumlahPO: tidakDibeli ? 0 : item.IsStock === false ? parseFloat(item.Qty) || 0 : jumlahPO,
      };
    });
  };

  const ModalOpener = (detail) => setShowModal(true);
  const param = useLocation();

  const mutationAdd = useMutation((formData) =>
    fetchPost("Procurement/PurchaseRequisition", formData)
  );
  const mutationGet = useMutation((formData) =>
    fetchGet("Procurement/PurchaseRequisition/" + formData)
  );
  const mutationUpdate = useMutation((formData) =>
    fetchPost("Procurement/PurchaseRequisition/approval", formData)
  );
  const priceComparison = useMutation((formData) =>
    fetchGet("Procurement/PriceComparisonFromPRID/" + formData)
  );

  const ModalEdit = (detail, id) => {
    setModalID(id);
    setModalDetail(detail);
    setShowModal(true);
    setShowModalEdit(true);
  };

  const ModalDelete = (id) => {
    let newDetail = [...detail];
    newDetail.splice(id, 1);
    setDetail(newDetail);
  };

  const navigate = useNavigate();

  useEffect(() => {
    barang.mutate();
    if (param.state) {
      mutationGet.mutate(param.state.PurchaseRequisitionID);
      priceComparison.mutate(param.state.PurchaseRequisitionID);
    }
  }, []);

  useEffect(() => {
    if (mutationUpdate.isSuccess) {
      navigate("/approval-list");
    }
  }, [mutationUpdate.status]);

  useEffect(() => {
    if (mutationGet.data) {
      const dataWithCalculations = updateAllData(mutationGet.data.data.PRList);
      setDetail(dataWithCalculations);
      // DEFAULT TERCENTANG SEMUA: menyetujui berarti meloloskan item apa adanya,
      // dan membatalkan harus disengaja dengan menghilangkan centang.
      //
      // Sebelumnya default-nya KOSONG agar approver memilih dari nol. Itu
      // menyimpan jebakan: satu klik Approve tanpa mencentang membatalkan seluruh
      // item sekaligus, dan pembatalan tak bisa dibalik (insiden 37 item
      // 01535/MSR/SO/07/2026). Ia juga membuat layar ini bertolak belakang dengan
      // approval borongan, yang mengirim ItemDibatalkan kosong = setujui semua.
      //
      // Yang disemai ke daftar batal hanyalah item yang SUDAH dibatalkan di level
      // approval sebelumnya — pembatalan bersifat satu arah, jadi item itu tetap
      // tak tercentang dan terkunci (lihat `terkunci`).
      setItemBatal(
        new Set(
          dataWithCalculations
            .filter((x) => x.Dibatalkan)
            .map((x) => String(x.PRListID))
        )
      );
      blastChange(mutationGet.data.data);
      setisJasa(mutationGet.data.data.Tipe === "Jasa");
    }
  }, [mutationGet.status]);

  const [fileViewer, setFileViewer] = useState({
    open: false, url: null, type: null, name: "", loading: false, error: false,
  });

  const openFileViewer = (rawUrl) => {
    const url = (rawUrl.name ? rawUrl.name : rawUrl).replaceAll("/", "-");
    const displayName = rawUrl.name ? rawUrl.name : rawUrl;
    setFileViewer({ open: true, url: null, type: null, name: displayName, loading: true, error: false });

    fetch(process.env.REACT_APP_API_UPLOAD_URL + "file/" + url, { method: "GET" })
      .then((response) => {
        const contentType = response.headers.get("Content-Type") || "";
        return response.blob().then((blob) => ({ blob, contentType }));
      })
      .then(({ blob, contentType }) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const isPdf = contentType.includes("pdf") || displayName.toLowerCase().endsWith(".pdf");
        const isImage = contentType.includes("image") ||
          /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(displayName);
        setFileViewer({
          open: true, url: blobUrl,
          type: isPdf ? "pdf" : isImage ? "image" : "pdf",
          name: displayName, loading: false, error: false,
        });
      })
      .catch(() => {
        setFileViewer((prev) => ({ ...prev, loading: false, error: true }));
        toast.warning("Data not Found!", {
          position: "top-center", autoClose: 1000, hideProgressBar: true,
          closeOnClick: true, pauseOnHover: false, draggable: true, theme: "light",
        });
      });
  };

  const closeFileViewer = () => {
    if (fileViewer.url) window.URL.revokeObjectURL(fileViewer.url);
    setFileViewer({ open: false, url: null, type: null, name: "", loading: false, error: false });
  };

  const fmt = (val, dec = 0) =>
    parseFloat(val || 0).toLocaleString("id-ID", { maximumFractionDigits: dec });

  const fmtDate = (val) => {
    if (!val) return "-";
    const d = new Date(val);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${d.getFullYear()}`;
  };

  const resolveBarang = (barangID) => {
    if (!barang.data?.data) return { name: barangID || "-", satuan: "-" };
    const found = barang.data.data.find(({ value }) => value === barangID);
    return { name: found?.label || barangID || "-", satuan: found?.NamaSatuan || "-" };
  };

  const requestAction = (type) => {
    setConfirmModal({ show: true, type: type === "1" ? "approve" : "reject" });
  };

  const confirmAction = () => {
    const type = confirmModal.type === "approve" ? "1" : "2";
    if (type === "2" && !rejectReason.trim()) return; // alasan wajib saat menolak
    // Konfirmasi tambahan (alert bawaan) sebelum benar-benar menolak — aksi tak bisa dibatalkan.
    if (
      type === "2" &&
      !window.confirm(
        "Yakin MENOLAK Purchase Requisition ini? Tindakan tidak dapat dibatalkan."
      )
    ) {
      return;
    }
    setConfirmModal({ show: false, type: null });
    mutationUpdate.mutate({
      PurchaseRequisitionID: param.state.PurchaseRequisitionID,
      Approved: type,
      KeteranganTolak: type === "2" ? rejectReason.trim() : "",
      // Hanya relevan saat approve; saat menolak, seluruh MSR gugur.
      ItemDibatalkan: type === "1" ? Array.from(itemBatal).map(Number) : [],
    });
    setRejectReason("");
  };

  const customHandlers = (event) => {
    customHandler(event.value, "PerkiraanHarga");
  };

  const isLoading = mutationGet.isLoading;
  const isSubmitting = mutationUpdate.isLoading;

  const itemDisetujui = detail.filter((x) => !itemBatal.has(String(x.PRListID)));
  const jumlahDibatalkan = detail.length - itemDisetujui.length;

  // ── Kontrol "Setujui Semua" ──────────────────────────────────
  // Hanya item yang bisa di-toggle (tidak terkunci) yang dihitung untuk keadaan
  // checkbox header. allChecked = semua item toggle-able sudah tercentang;
  // sebagian tercentang → tampil indeterminate (garis).
  const selectableItems = detail.filter((item) => !terkunci(item));
  const allChecked =
    selectableItems.length > 0 &&
    selectableItems.every((item) => !itemBatal.has(String(item.PRListID)));
  const someChecked = selectableItems.some(
    (item) => !itemBatal.has(String(item.PRListID))
  );

  // Penjaga: Approve dimatikan bila TIDAK ADA satu pun item yang bisa dibeli
  // tercentang — sebab menyetujui begitu berarti membatalkan seluruh item, dan
  // pembatalan tak bisa dibalik.
  //
  // Sejak default diubah menjadi tercentang semua, keadaan ini tak lagi terjadi
  // secara tak sengaja saat halaman dibuka; ia hanya tercapai kalau approver
  // sendiri menghilangkan semua centang. Penjaga ini TETAP DIPERLUKAN untuk
  // menahan langkah di situ.
  //
  // Dasarnya WAJIB `someChecked` (item non-stok), bukan `itemDisetujui` yang
  // menghitung seluruh baris: item ber-IsStock selalu tercentang & terkunci,
  // jadi pada MSR campuran ia membuat penjaga ini selalu lolos. Itulah yang
  // terjadi pada 01535/MSR/SO/07/2026 di prod — 4 item stok membuat tombol
  // Approve aktif, satu klik membatalkan 37 item non-stok sekaligus, MSR lolos
  // auto-tolak backend (sisaItem = 4), lalu form Price Comparison menerima
  // PRList kosong.
  //
  // selectableItems.length === 0 (semua item ber-stok) TIDAK dimatikan: tak ada
  // yang bisa dibatalkan di situ, jadi Approve memang tak berisiko.
  // Tombol Tolak sengaja TIDAK ikut dimatikan: menolak MSR utuh tetap sah.
  //
  // Logika auto-tolak di backend TETAP ada dan tidak boleh dihapus: request
  // bisa datang langsung ke API tanpa lewat halaman ini.
  const tidakAdaItemDisetujui = selectableItems.length > 0 && !someChecked;

  const selectAllRef = useRef(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someChecked && !allChecked;
    }
  }, [someChecked, allChecked]);

  const FileViewerModal = () => {
    if (!fileViewer.open) return null;
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-black bg-opacity-80">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-900 text-white shrink-0">
          <div className="flex items-center gap-2 text-sm">
            {fileViewer.type === "image"
              ? <BiImage className="text-blue-400 text-lg" />
              : <BiFile className="text-red-400 text-lg" />}
            <span className="truncate max-w-xs opacity-80">{fileViewer.name}</span>
          </div>
          <div className="flex items-center gap-3">
            {fileViewer.url && (
              <a
                href={fileViewer.url}
                download={fileViewer.name}
                className="text-xs px-3 py-1.5 bg-white text-gray-800 rounded hover:bg-gray-200 transition-colors font-medium"
              >
                Download
              </a>
            )}
            <button
              onClick={closeFileViewer}
              className="p-1.5 rounded-full hover:bg-gray-700 transition-colors"
              aria-label="Tutup"
            >
              <BiX className="text-2xl" />
            </button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center overflow-hidden bg-gray-800">
          {fileViewer.loading && (
            <div className="flex flex-col items-center gap-3 text-white">
              <BiLoader className="text-4xl animate-spin" />
              <span className="text-sm opacity-70">Memuat file...</span>
            </div>
          )}
          {fileViewer.error && (
            <div className="text-center text-white">
              <p className="text-lg font-semibold mb-1">File tidak ditemukan</p>
              <p className="text-sm opacity-60">Pastikan file masih tersedia di server.</p>
            </div>
          )}
          {fileViewer.url && fileViewer.type === "pdf" && (
            <iframe
              src={fileViewer.url}
              title="Preview PDF"
              className="w-full h-full border-0"
              style={{ minHeight: "80vh" }}
            />
          )}
          {fileViewer.url && fileViewer.type === "image" && (
            <img
              src={fileViewer.url}
              alt="Preview"
              className="max-w-full max-h-full object-contain p-4"
              style={{ maxHeight: "calc(100vh - 56px)" }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="md:ml-64">
      <FileViewerModal />
      <ConfirmModal
        show={confirmModal.show}
        type={confirmModal.type}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        jumlahDibatalkan={jumlahDibatalkan}
        jumlahDisetujui={itemDisetujui.length}
        onConfirm={confirmAction}
        onCancel={() => {
          setConfirmModal({ show: false, type: null });
          setRejectReason("");
        }}
      />

      <div
        style={{ background: Colors.primary }}
        className="pt-14 pb-16 px-3 md:px-8 h-auto"
      >
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4" />
        </div>
      </div>

      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 px-4">
            <Card>
              <div className="flex flex-col mt-5 px-2">

                {/* Header bar: judul + tombol aksi */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">
                      Approval Purchase Requisition
                    </h2>
                    {values.NoPR && (
                      <p className="text-sm text-gray-500 mt-0.5">No. PR: {values.NoPR}</p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate("/approval-list")}
                      disabled={isSubmitting}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm transition-colors disabled:opacity-50"
                    >
                      <BiArrowBack className="text-base" />
                      Kembali
                    </button>

                    <button
                      onClick={() => requestAction("2")}
                      disabled={isSubmitting || isLoading}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                    >
                      {isSubmitting && confirmModal.type === "reject" ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                          Memproses...
                        </>
                      ) : (
                        <>
                          <BiXCircle className="text-base" />
                          Tolak
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => requestAction("1")}
                      disabled={isSubmitting || isLoading || tidakAdaItemDisetujui}
                      title={
                        tidakAdaItemDisetujui
                          ? "Centang minimal satu item yang perlu dibeli terlebih dahulu. Item stok yang terkunci tidak dihitung — menyetujui tanpa mencentang apa pun akan membatalkan seluruh item pembelian MSR ini, dan itu tak bisa dibalik."
                          : "Setujui MSR ini"
                      }
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-500"
                    >
                      {isSubmitting && confirmModal.type === "approve" ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                          Memproses...
                        </>
                      ) : (
                        <>
                          <BiCheckCircle className="text-base" />
                          Approve
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <hr className="mb-1" />

                {isLoading ? (
                  <div className="flex flex-col gap-3 py-6 px-2 animate-pulse">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="h-9 bg-gray-100 rounded-md w-full" />
                    ))}
                  </div>
                ) : (
                  <>
                    {/* Informasi Umum */}
                    <SectionTitle title="Informasi Umum" />
                    <FieldGroup cols={3}>
                      <HrdInputGenerator
                        type={"BisnisUnit"}
                        value={values}
                        name={"BisnisUnitID"}
                        title={"Bisnis Unit"}
                        onChange={customHandler}
                        touched={touched}
                        buttonTouched={buttonTouched}
                        size={"full"}
                        required={true}
                        errors={errors}
                        disabled={true}
                      />
                      <HrdInputGenerator
                        type={"Bagian"}
                        value={values}
                        name={"BagianID"}
                        title={"Bagian"}
                        onChange={customHandler}
                        touched={touched}
                        buttonTouched={buttonTouched}
                        size={"full"}
                        required={true}
                        errors={errors}
                        disabled={true}
                      />
                      <HrdInputGenerator
                        type={"dateLock"}
                        value={values}
                        name={"DatePR"}
                        title={"Tanggal MSR"}
                        onChange={customHandler}
                        touched={touched}
                        buttonTouched={buttonTouched}
                        size={"full"}
                        required={true}
                        errors={errors}
                        disabled={true}
                      />
                      <HrdInputGenerator
                        type={"TipePR"}
                        value={values}
                        name={"Tipe"}
                        title={"Tipe"}
                        onChange={customHandler}
                        touched={touched}
                        buttonTouched={buttonTouched}
                        size={"full"}
                        required={true}
                        errors={errors}
                        disabled={true}
                      />
                      <HrdInputGenerator
                        type={"Sifat"}
                        value={values}
                        name={"Sifat"}
                        title={"Sifat Kebutuhan Barang"}
                        onChange={customHandler}
                        touched={touched}
                        buttonTouched={buttonTouched}
                        size={"full"}
                        required={true}
                        errors={errors}
                        disabled={true}
                      />
                      <HrdInputGenerator
                        type={"text"}
                        value={values}
                        name={"Requester"}
                        title={"Pemohon"}
                        onChange={changeHandler}
                        touched={touched}
                        buttonTouched={buttonTouched}
                        size={"full"}
                        required={true}
                        errors={errors}
                        disabled={true}
                      />
                    </FieldGroup>

                    {/* Informasi Keuangan */}
                    <SectionTitle title="Informasi Keuangan" />
                    <FieldGroup cols={3}>
                      <HrdInputGenerator
                        type={"numberFormatSatu"}
                        value={values}
                        name={"PerkiraanHarga"}
                        title={"Perkiraan Harga"}
                        onChange={customHandlers}
                        touched={touched}
                        buttonTouched={buttonTouched}
                        size={"full"}
                        required={true}
                        errors={errors}
                        disabled={true}
                      />
                      <div className="sm:col-span-2">
                        <HrdInputGenerator
                          type={"text"}
                          value={values}
                          name={"Remarks"}
                          title={"Keterangan"}
                          onChange={changeHandler}
                          touched={touched}
                          buttonTouched={buttonTouched}
                          size={"full"}
                          required={true}
                          errors={errors}
                          disabled={true}
                        />
                      </div>
                    </FieldGroup>

                    {/* Termin (jasa) */}
                    {isJasa && (values.Termin1 > 0 || values.Termin2 > 0 || values.Termin3 > 0) && (
                      <>
                        <SectionTitle title="Termin Pembayaran" />
                        <FieldGroup cols={3}>
                          {isJasa && values.Termin1 > 0 && (
                            <HrdInputGenerator
                              type={"Termin"}
                              value={values}
                              name={"Termin1"}
                              title={"Termin 1 (%)"}
                              onChange={changeHandler}
                              touched={touched}
                              buttonTouched={buttonTouched}
                              size={"full"}
                              required={false}
                              errors={errors}
                              disabled={true}
                              priceComparison={priceComparison}
                              isApproval={false}
                            />
                          )}
                          {isJasa && values.Termin2 > 0 && (
                            <HrdInputGenerator
                              type={"Termin"}
                              value={values}
                              name={"Termin2"}
                              title={"Termin 2 (%)"}
                              onChange={changeHandler}
                              touched={touched}
                              buttonTouched={buttonTouched}
                              size={"full"}
                              required={false}
                              priceComparison={priceComparison}
                              errors={errors}
                              disabled={true}
                              isApproval={false}
                            />
                          )}
                          {isJasa && values.Termin3 > 0 && (
                            <HrdInputGenerator
                              type={"Termin"}
                              value={values}
                              name={"Termin3"}
                              title={"Termin 3 (%)"}
                              onChange={changeHandler}
                              touched={touched}
                              buttonTouched={buttonTouched}
                              priceComparison={priceComparison}
                              size={"full"}
                              required={false}
                              errors={errors}
                              disabled={true}
                              isApproval={false}
                            />
                          )}
                        </FieldGroup>
                      </>
                    )}

                    {/* Data Pendukung */}
                    {values["NamaFile"] && (
                      <>
                        <SectionTitle title="Data Pendukung" />
                        <div className="mb-4">
                          <button
                            onClick={() => openFileViewer(values["NamaFile"])}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            {/\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(
                              values["NamaFile"].name ?? values["NamaFile"]
                            ) ? (
                              <BiImage className="text-lg" />
                            ) : (
                              <BiFile className="text-lg" />
                            )}
                            {values["NamaFile"].name ? values["NamaFile"].name : values["NamaFile"]}
                            <span className="text-xs opacity-60 shrink-0">— klik untuk lihat</span>
                          </button>
                        </div>
                      </>
                    )}

                    <hr className="my-3" />

                    {/* Detail Barang */}
                    <SectionTitle title="Detail Barang" />
                    {detail.length > 0 ? (
                      <>
                      <div
                        className="-mx-2 mb-1 border-y border-gray-200"
                        style={{ overflowX: "scroll", WebkitOverflowScrolling: "touch" }}
                      >
                        <table className="table-auto text-sm text-left text-gray-600 min-w-full" style={{ minWidth: "700px" }}>
                          <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0">
                            <tr>
                              <th
                                className="py-3 px-4 text-center"
                                title="Centang item yang disetujui. Item yang tidak dicentang akan dibatalkan."
                              >
                                <div className="flex flex-col items-center gap-1">
                                  <input
                                    ref={selectAllRef}
                                    type="checkbox"
                                    checked={allChecked}
                                    disabled={selectableItems.length === 0}
                                    onChange={(e) => setAllItemBatal(!e.target.checked)}
                                    className={`h-4 w-4 accent-green-600 ${
                                      selectableItems.length === 0
                                        ? "cursor-not-allowed opacity-60"
                                        : "cursor-pointer"
                                    }`}
                                    aria-label="Setujui semua item"
                                    title="Centang semua / hilangkan centang semua item"
                                  />
                                  <span className="text-[10px] font-normal normal-case leading-none">
                                    Setujui
                                  </span>
                                </div>
                              </th>
                              <th className="py-3 px-4 text-center">No</th>
                              <th className="py-3 px-4">Barang / Jasa</th>
                              <th className="py-3 px-4">Satuan</th>
                              <th className="py-3 px-4 text-right">Saldo Akhir</th>
                              <th className="py-3 px-4 text-right">Min Qty</th>
                              <th className="py-3 px-4 text-right">Qty Transit</th>
                              <th className="py-3 px-4 text-right">Stock Tersedia</th>
                              <th className="py-3 px-4 text-right">Jml Pengajuan</th>
                              <th className="py-3 px-4 text-right">Jumlah PO</th>
                              <th className="py-3 px-4">Keterangan</th>
                              <th className="py-3 px-4 text-center">Tgl Dibutuhkan</th>
                              <th className="py-3 px-4">Spesifikasi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.map((item, idx) => {
                              const { name: namaBarang, satuan } = resolveBarang(item.BarangID);
                              const needPO = parseFloat(item.JumlahPO || 0) > 0;
                              const stockLow = parseFloat(item.Stock || 0) === 0;
                              const dibatalkan = itemBatal.has(String(item.PRListID));
                              const isTerkunci = terkunci(item);
                              const rowBase = idx % 2 === 0 ? "bg-white" : "bg-gray-50";
                              const rowClass = dibatalkan
                                ? "bg-red-50 border-b border-red-100 text-gray-400 line-through"
                                : needPO
                                ? "bg-orange-50 border-b border-orange-100 hover:bg-orange-100"
                                : `${rowBase} border-b hover:bg-gray-100`;

                              const judulKunci = item.IsStock
                                ? "Tidak dapat dibatalkan — barang sudah dikeluarkan dari gudang saat MSR dikirim"
                                : item.Dibatalkan
                                ? "Sudah dibatalkan pada approval sebelumnya dan tidak dapat dihidupkan kembali"
                                : dibatalkan
                                ? "Centang untuk menyetujui item ini"
                                : "Hilangkan centang untuk membatalkan item ini";

                              return (
                                <tr key={idx} className={rowClass}>
                                  <td className="py-3 px-4 text-center" title={judulKunci}>
                                    <input
                                      type="checkbox"
                                      checked={!dibatalkan}
                                      disabled={isTerkunci}
                                      onChange={() => toggleItemBatal(item)}
                                      className={`h-4 w-4 accent-green-600 ${
                                        isTerkunci ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                                      }`}
                                      aria-label={`Setujui ${namaBarang}`}
                                    />
                                  </td>
                                  <td className="py-3 px-4 text-center text-gray-400 text-xs">{idx + 1}</td>
                                  <td className="py-3 px-4 font-medium text-gray-800 whitespace-nowrap">{namaBarang}</td>
                                  <td className="py-3 px-4 whitespace-nowrap">{satuan}</td>
                                  <td className="py-3 px-4 text-right whitespace-nowrap">{fmt(item.SaldoAkhir, 2)}</td>
                                  <td className="py-3 px-4 text-right whitespace-nowrap">{fmt(item.MinQty, 2)}</td>
                                  <td className="py-3 px-4 text-right whitespace-nowrap">{fmt(item.QtyTransit, 2)}</td>
                                  <td className={`py-3 px-4 text-right whitespace-nowrap font-medium ${stockLow ? "text-red-600" : "text-green-700"}`}>
                                    {fmt(item.Stock, 2)}
                                  </td>
                                  <td className="py-3 px-4 text-right whitespace-nowrap">{fmt(item.Qty, 2)}</td>
                                  <td className={`py-3 px-4 text-right whitespace-nowrap font-semibold ${needPO ? "text-orange-600" : "text-gray-500"}`}>
                                    {fmt(item.JumlahPO, 2)}
                                  </td>
                                  <td className={`py-3 px-4 whitespace-nowrap ${item.MenungguBarang ? "text-blue-700" : needPO && !item.IsStock ? "text-orange-600" : "text-green-700"}`}>
                                    {item.MenungguBarang
                                      ? "Menunggu Barang (Transit)"
                                      : needPO && !item.IsStock
                                      ? "Pembelian Barang"
                                      : "Barang Tersedia"}
                                  </td>
                                  <td className="py-3 px-4 text-center whitespace-nowrap">{fmtDate(item.SDTDate)}</td>
                                  <td className="py-3 px-4 whitespace-nowrap">{item.Specification || "-"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="text-xs font-semibold text-gray-700 bg-gray-100 border-t-2 border-gray-300">
                            {/* Total hanya menjumlahkan item yang MASIH disetujui —
                                menghitung item batal akan menyesatkan approver. */}
                            <tr>
                              <td colSpan={8} className="py-3 px-4 text-right">
                                Total (item disetujui)
                              </td>
                              <td className="py-3 px-4 text-right">
                                {fmt(itemDisetujui.reduce((s, r) => s + parseFloat(r.Qty || 0), 0), 2)}
                              </td>
                              <td className={`py-3 px-4 text-right ${itemDisetujui.some(r => parseFloat(r.JumlahPO || 0) > 0) ? "text-orange-600" : ""}`}>
                                {fmt(itemDisetujui.reduce((s, r) => s + parseFloat(r.JumlahPO || 0), 0), 2)}
                              </td>
                              <td colSpan={3} />
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      <div className="px-2 py-2 mb-4 space-y-1">
                        <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                          <span className="font-semibold">Semua item tercentang secara default.</span>{" "}
                          Hilangkan centang hanya pada item yang ingin
                          <b> dibatalkan</b> — item itu tidak akan diproses
                          pembeliannya, dan pembatalan tidak dapat dibatalkan.
                        </p>
                        {detail.some(r => parseFloat(r.JumlahPO || 0) > 0) && (
                          <p className="text-xs text-orange-600">
                            <span className="font-semibold">Baris oranye</span> = item yang perlu dibuat PO karena stock tidak mencukupi.
                          </p>
                        )}
                        {detail.some(r => r.IsStock) && (
                          <p className="text-xs text-gray-500">
                            <span className="font-semibold">Centang terkunci</span> = barang sudah dikeluarkan dari gudang saat MSR dikirim, jadi item ini tidak dapat dibatalkan.
                          </p>
                        )}
                        {jumlahDibatalkan > 0 && (
                          <p className="text-xs text-red-600">
                            <span className="font-semibold">Baris dicoret</span> = {jumlahDibatalkan} item tidak tercentang, jadi akan dibatalkan.
                          </p>
                        )}
                      </div>
                      </>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50 mb-4">
                        <svg
                          className="w-10 h-10 text-gray-400 mx-auto mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                          />
                        </svg>
                        <p className="text-sm text-gray-500">Tidak ada data barang.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalPurchaseRequisitionPage;
